import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Building2, ArrowLeft, Moon, Sun } from 'lucide-react';

const ClientArea = ({ onBack }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const applications = [
    {
      id: 'management-clienti',
      name: 'Management Clienți',
      description: 'Administrare relații cu clienții',
      icon: Users,
      iconBg: 'bg-rose-100 dark:bg-rose-900/30',
      iconColor: 'text-rose-600 dark:text-rose-400'
    }
  ];

  const handleAppClick = (appId) => {
    setSelectedApp(appId);
    setTimeout(() => {
      console.log(`Launching ${appId}...`);
      window.open('/' + appId, '_blank'); // opens in new tab
      setSelectedApp(null);
    }, 300);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={onBack}
              className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-elegant-md">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Client Area</h1>
              <p className="text-xs text-muted-foreground">Management clienți și șantiere</p>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={toggleDarkMode}
            className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4">
              Client Area
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Aplicații dedicate pentru managementul clienților și coordonarea șantierelor.
            </p>
          </div>

          {/* Applications Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-1 gap-6 max-w-md mx-auto animate-scale-in">
            {applications.map((app, index) => {
              const Icon = app.icon;
              const isSelected = selectedApp === app.id;
              
              return (
                <Card
                  key={app.id}
                  className={`group relative bg-card border-border hover:border-emerald-500/50 rounded-2xl p-6 cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-elegant-lg hover:-translate-y-1 ${
                    isSelected ? 'scale-95 opacity-50' : ''
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => handleAppClick(app.id)}
                >
                  {/* Hover gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/0 via-emerald-500/0 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  <div className="relative z-10">
                    {/* Icon */}
                    <div className={`${app.iconBg} w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-7 h-7 ${app.iconColor}`} />
                    </div>
                    
                    {/* Content */}
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold text-foreground group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-300">
                        {app.name}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {app.description}
                      </p>
                    </div>
                    
                    {/* Arrow indicator */}
                    <div className="mt-4 flex items-center text-emerald-600 dark:text-emerald-400 text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-1">
                      <span>Lansează</span>
                      <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientArea;
